import { useState } from "react";
import { TextInput } from "../../UI/TextInput.tsx";
import { InputWithLabel } from "../../UI/InputWithLabel.tsx";
import { PrimaryButton } from "../../UI/PrimaryButton.tsx";
import { Select } from "@normasteaching/norma-ui";
import {
  ManualBonusType,
  ManualBonusFormInput,
  toBonusDateInputValue,
} from "./planBonusUtils.ts";

const BONUS_TYPE_OPTIONS: { id: ManualBonusType; name: string }[] = [
  { id: "all_you_can_speak", name: "All You Can Speak" },
  { id: "extra_lessons", name: "Extra Lessons" },
  { id: "extra_weeks", name: "Extra Weeks" },
];

const BONUS_TYPE_DESCRIPTIONS: Record<ManualBonusType, string> = {
  all_you_can_speak:
    "Dalle lezioni conteggiate in poi, l'utente può prenotare liberamente fino a domenica.",
  extra_lessons:
    "Aggiunge un numero fisso di lezioni extra al monte ore totale del piano.",
  extra_weeks: "Aggiunge settimane extra alla durata del piano.",
};

type Props = {
  onConfirm: (bonus: ManualBonusFormInput) => void;
  onClose: () => void;
  initialValue?: ManualBonusFormInput;
};

export function AddPlanBonusModal({ onConfirm, onClose, initialValue }: Props) {
  const isEditing = initialValue !== undefined;
  const [type, setType] = useState<ManualBonusType>(
    initialValue?.type ?? "all_you_can_speak",
  );
  const [lessonsCountedPerWeek, setLessonsCountedPerWeek] = useState(
    initialValue?.lessonsCountedPerWeek ?? 2,
  );
  const [extraLessons, setExtraLessons] = useState(
    initialValue?.extraLessons ?? 1,
  );
  const [extraWeeks, setExtraWeeks] = useState(initialValue?.extraWeeks ?? 1);
  // The stored values are UTC instants; a date input only accepts YYYY-MM-DD and silently
  // renders blank otherwise, which would make an edit wipe the window it was meant to adjust.
  const [activeFrom, setActiveFrom] = useState(
    toBonusDateInputValue(initialValue?.activeFrom),
  );
  const [activeTo, setActiveTo] = useState(
    toBonusDateInputValue(initialValue?.activeTo),
  );
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = () => {
    if (type === "all_you_can_speak") {
      if (lessonsCountedPerWeek < 0 || isNaN(lessonsCountedPerWeek)) {
        setError("Inserisci un numero di lezioni valido (minimo 0)");
        return;
      }
      setError(null);
      onConfirm({
        type,
        lessonsCountedPerWeek,
        activeFrom: activeFrom || undefined,
        activeTo: activeTo || undefined,
      });
      return;
    }
    if (type === "extra_lessons") {
      if (extraLessons <= 0 || isNaN(extraLessons)) {
        setError("Inserisci un numero di lezioni extra valido (minimo 1)");
        return;
      }
      setError(null);
      onConfirm({ type, extraLessons });
      return;
    }
    if (extraWeeks <= 0 || isNaN(extraWeeks)) {
      setError("Inserisci un numero di settimane extra valido (minimo 1)");
      return;
    }
    setError(null);
    onConfirm({ type, extraWeeks });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 flex flex-col gap-5">
        <h3 className="text-lg font-bold">
          {isEditing ? "Edit bonus" : "Add bonus"}
        </h3>

        <InputWithLabel label="Bonus type">
          <Select<ManualBonusType>
            value={type}
            onChange={(v) => {
              setType(v as ManualBonusType);
              setError(null);
            }}
            options={BONUS_TYPE_OPTIONS}
          />
          <p className="text-xs text-gray-500 mt-1">
            {BONUS_TYPE_DESCRIPTIONS[type]}
          </p>
        </InputWithLabel>

        {type === "all_you_can_speak" && (
          <>
            <InputWithLabel label="Lessons counted per week">
              <TextInput
                type="number"
                value={lessonsCountedPerWeek}
                onChange={(e) =>
                  setLessonsCountedPerWeek(parseInt(e.target.value, 10))
                }
                errorMessage={error ?? undefined}
              />
              {lessonsCountedPerWeek === 0 && (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
                  <span className="mt-0.5 text-yellow-500">⚠</span>
                  <span>
                    Con 0 lezioni conteggiate il piano diventa{" "}
                    <strong>illimitato</strong>: l'utente potrà prenotare senza
                    limiti per tutta la durata del bonus e non vedrà il
                    contatore delle lezioni rimanenti.
                  </span>
                </div>
              )}
            </InputWithLabel>
            <InputWithLabel label="Active from (optional)">
              <TextInput
                type="date"
                value={activeFrom}
                onChange={(e) => setActiveFrom(e.target.value)}
              />
            </InputWithLabel>
            <InputWithLabel label="Active to (optional)">
              <TextInput
                type="date"
                value={activeTo}
                onChange={(e) => setActiveTo(e.target.value)}
              />
            </InputWithLabel>
          </>
        )}

        {type === "extra_lessons" && (
          <InputWithLabel label="Extra lessons">
            <TextInput
              type="number"
              value={extraLessons}
              onChange={(e) => setExtraLessons(parseInt(e.target.value, 10))}
              errorMessage={error ?? undefined}
            />
          </InputWithLabel>
        )}

        {type === "extra_weeks" && (
          <InputWithLabel label="Extra weeks">
            <TextInput
              type="number"
              value={extraWeeks}
              onChange={(e) => setExtraWeeks(parseInt(e.target.value, 10))}
              errorMessage={error ?? undefined}
            />
          </InputWithLabel>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Cancel
          </button>
          <PrimaryButton
            label={isEditing ? "Save bonus" : "Add bonus"}
            onClick={handleConfirm}
          />
        </div>
      </div>
    </div>
  );
}
